<template>
    <div class="content">
        <div class="container-fluid">
            <div class="preview-page">
                <div class="preview-layout">
                    <div class="card preview-sidebar">
                        <div class="loader" v-if="waiting"></div>
                        <label class="preview-dev-toggle">
                            <input type="checkbox" v-model="showInactive">
                            Показать скрытые
                        </label>
                        <div class="preview-sidebar-title">Категории</div>
                        <div class="preview-sidebar-list">
                            <draggable v-model="data"
                                       @start="onCategoriesDragStart"
                                       @end="onCategoriesDragEnd"
                                       @change="onCategoriesReorder">
                                <transition-group>
                                    <div class="category-list"
                                         v-for="element in data"
                                         :key="element.id"
                                         :class="{ active: isSelectedCategory(element.id), 'is-inactive': isCategoryInactive(element.status) }"
                                         @click="selectCategory(element.id)">
                                        <span class="category-list-row">
                                            <span class="category-list-name">{{ element.name }}</span>
                                            <span class="category-off" v-if="isCategoryInactive(element.status)">Скрыта</span>
                                            <span class="category-list-count">{{ element.items ? element.items.length : element.items_count }}</span>
                                        </span>
                                    </div>
                                </transition-group>
                            </draggable>
                        </div>
                    </div>

                    <div class="card preview-main">
                        <div class="loader" v-if="waiting"></div>
                        <div class="app-area">
                            <div v-if="selectedCategory">
                                <p class="category-name">
                                    {{ selectedCategory.name }}
                                    <span class="category-count">({{ visibleItems.length }})</span>
                                    <span class="category-off-note" v-if="isCategoryInactive(selectedCategory.status)">
                                        Категория не отображается в приложении
                                    </span>
                                </p>

                                <div v-if="visibleItems.length > 0"
                                     class="items-grid"
                                     :key="selectedCategory.id">
                                    <draggable v-model="visibleItems"
                                               @start="onItemsDragStart"
                                               @end="onItemsDragEnd"
                                               @change="onItemsReorder(selectedCategory)">
                                        <div v-for="item in visibleItems" :key="item.id"
                                             class="inline-div"
                                             @click.prevent="openItem(item.id)">
                                            <div class="card-item">
                                                <img class="card-media" :src="item.src_full" loading="lazy" alt="">
                                                <span class="item-badge is-id">ID {{ item.id }}</span>
                                                <span class="item-badge is-dev" v-if="isInactive(item.status)">Скрыт</span>
                                            </div>
                                        </div>
                                    </draggable>
                                </div>
                                <p v-else class="preview-empty">{{ emptyLabel }}</p>
                            </div>
                            <div class="preview-main-footer">
                                <button type="submit" class="btn btn-success btn-fill btn-save"
                                        :disabled="!hasUnsavedOrder"
                                        @click.prevent="save()">
                                    Сохранить
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
const _ = require('lodash')
const axios = require('axios')
import draggable from 'vuedraggable'
import previewPage from 'src/mixins/previewPage'

// Scroll position of the preview, kept while an item is open in the edit page,
// so "back" returns to the same place. Lives in memory only.
let pendingScroll = null;

export default {
    mixins: [previewPage],
    components: {
        draggable
    },
    props: {
        // api prefix: /admin/{resource}/preview
        resource: {
            type: String,
            required: true
        },
        editRoute: {
            type: String,
            required: true
        },
        emptyLabel: {
            type: String,
            default: 'Пусто'
        }
    },
    beforeRouteEnter: function (to, from, next) {
        // the edit page opened from the preview carries its address in ?back
        if (!pendingScroll || pendingScroll.path !== to.fullPath || from.query.back !== to.fullPath) {
            pendingScroll = null;
        }
        next();
    },
    created: function () {
        this.scrollToRestore = pendingScroll ? pendingScroll.top : null;
        pendingScroll = null;
        this.get();
    },
    data() {
        return {
            data: [],
            waiting: false,
            categoryId: parseInt(this.$route.query.category, 10) || null,
            itemOrders: [],
            categoryOrderDirty: false,
            dragGuard: false,
            categoryDragGuard: false,
            showInactive: true,
        }
    },
    computed: {
        selectedCategory: function () {
            const categoryId = parseInt(this.categoryId, 10);
            return _.find(this.data, function (category) {
                return parseInt(category.id, 10) === categoryId;
            }) || null;
        },
        hasUnsavedOrder: function () {
            return this.itemOrders.length > 0 || this.categoryOrderDirty;
        },
        visibleItems: {
            get: function () {
                const category = this.selectedCategory;
                if (!category || !category.items) {
                    return [];
                }
                if (this.showInactive) {
                    return category.items;
                }
                const self = this;
                return _.filter(category.items, function (item) {
                    return !self.isInactive(item.status);
                });
            },
            set: function (items) {
                const category = this.selectedCategory;
                if (!category) {
                    return;
                }
                if (this.showInactive) {
                    category.items = items;
                    return;
                }
                const self = this;
                const queue = items.slice();
                category.items = _.map(category.items, function (item) {
                    if (self.isInactive(item.status)) {
                        return item;
                    }
                    return queue.length ? queue.shift() : item;
                });
            }
        }
    },
    watch: {
        categoryId: function (id) {
            this.loadItems(this.selectedCategory);
            if (!id || parseInt(this.$route.query.category, 10) === parseInt(id, 10)) {
                return;
            }
            this.$router.replace({query: Object.assign({}, this.$route.query, {category: String(id)})});
        }
    },
    methods: {
        get: function () {
            let self = this;

            let token = localStorage.getItem('token');

            self.waiting = true;

            axios.get(
                `${this.$hostname}/admin/${this.resource}/preview`,
                {
                    'headers': {
                        'Authorization': 'Bearer ' + token
                    }
                })
                .then(function (response) {
                    self.waiting = false;
                    self.itemOrders = [];
                    self.categoryOrderDirty = false;
                    // items are loaded per category when it is opened
                    self.data = _.map(response.data || [], function (category) {
                        category.items = null;
                        return category;
                    });
                    const selectedExists = _.some(self.data, function (category) {
                        return parseInt(category.id, 10) === parseInt(self.categoryId, 10);
                    });
                    if (!selectedExists && self.data.length) {
                        self.categoryId = self.data[0].id;
                    } else {
                        self.loadItems(self.selectedCategory);
                    }
                })
                .catch(function (error) {
                    self.waiting = false;
                    console.log(error)
                })

        },
        save: function () {
            let self = this;

            let token = localStorage.getItem('token');

            self.waiting = true;

            axios.post(
                `${this.$hostname}/admin/${this.resource}/preview/resort`,
                {
                    categoryIds: this.categoryOrderDirty ? _.map(this.data, 'id') : [],
                    itemOrders: this.itemOrders,
                },
                {
                    'headers': {
                        'Authorization': 'Bearer ' + token
                    }
                })
                .then(function () {
                    self.itemOrders = [];
                    self.categoryOrderDirty = false;
                    self.get();
                })
                .catch(function (error) {
                    self.waiting = false;
                    console.log(error)
                })

        },
        onItemsDragStart: function () {
            this.dragGuard = true;
        },
        onItemsDragEnd: function () {
            setTimeout(() => {
                this.dragGuard = false;
            }, 150);
        },
        onCategoriesDragStart: function () {
            this.categoryDragGuard = true;
        },
        onCategoriesDragEnd: function () {
            setTimeout(() => {
                this.categoryDragGuard = false;
            }, 150);
        },
        onCategoriesReorder: function () {
            this.categoryOrderDirty = true;
        },
        isSelectedCategory: function (id) {
            return parseInt(id, 10) === parseInt(this.categoryId, 10);
        },
        selectCategory: function (id) {
            if (this.categoryDragGuard) {
                return;
            }
            this.categoryId = id;
        },
        loadItems: function (category) {
            if (!category || category.items || category.itemsLoading) {
                return;
            }
            let self = this;

            let token = localStorage.getItem('token');

            category.itemsLoading = true;
            self.waiting = true;

            axios.get(
                `${this.$hostname}/admin/${this.resource}/preview/items`,
                {
                    'params': {
                        categoryId: category.id,
                    },
                    'headers': {
                        'Authorization': 'Bearer ' + token
                    }
                })
                .then(function (response) {
                    category.itemsLoading = false;
                    category.items = response.data || [];
                    self.waiting = false;
                    self.restoreScroll();
                })
                .catch(function (error) {
                    category.itemsLoading = false;
                    self.waiting = false;
                    console.log(error)
                })
        },
        scrollContainer: function () {
            return document.querySelector('.main-panel');
        },
        restoreScroll: function () {
            const top = this.scrollToRestore;
            this.scrollToRestore = null;
            if (top === null || top === undefined) {
                return;
            }
            this.$nextTick(() => {
                const container = this.scrollContainer();
                if (container) {
                    container.scrollTop = top;
                }
            });
        },
        onItemsReorder: function (category) {
            if (!category || !category.items) {
                return;
            }

            const categoryId = parseInt(category.id, 10);
            this.itemOrders = _.filter(this.itemOrders, function (order) {
                return parseInt(order.categoryId, 10) !== categoryId;
            });
            this.itemOrders.push({
                categoryId: categoryId,
                ids: _.map(category.items, 'id'),
            });
        },
        isInactive: function (status) {
            return parseInt(status, 10) === 0;
        },
        openItem: function (id) {
            if (this.dragGuard) {
                return;
            }
            const container = this.scrollContainer();
            pendingScroll = {path: this.$route.fullPath, top: container ? container.scrollTop : 0};
            this.$router.push({
                name: this.editRoute,
                params: {id: id},
                query: {back: this.$route.fullPath}
            });
        },
    }
}
</script>
<style scoped>

@import url('https://fonts.cdnfonts.com/css/sf-pro-display');

.preview-page {
    position: relative;
}

.preview-main-footer {
    margin: 24px 8px 4px;
}

.btn-save {
    width: 150px;
}

.preview-layout {
    display: flex;
    align-items: stretch;
    gap: 16px;
}

.preview-sidebar {
    width: 240px;
    flex-shrink: 0;
    margin-bottom: 0;
    position: relative;
    padding: 12px;
}

.preview-sidebar-title {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #9a9a9a;
    margin-bottom: 8px;
}

.preview-sidebar-list {
    max-height: calc(100vh - 220px);
    overflow-y: auto;
}

.preview-main {
    flex: 1;
    min-width: 0;
    margin-bottom: 0;
    position: relative;
}

.category-name {
    margin: 0 0 12px 8px;
    font-family: 'SF Pro Display', sans-serif !important;
    font-weight: 500;
    font-size: 15px;
    letter-spacing: 0.5px;
}

.preview-dev-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 26px 0;
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    color: #5e5e5e;
    white-space: nowrap;
    cursor: pointer;
}

.preview-dev-toggle input {
    margin: 0;
}

.category-count {
    color: #9a9a9a;
    font-weight: 400;
}

.preview-empty {
    margin: 0 8px 16px;
    color: #9a9a9a;
}

.app-area {
    width: 100%;
    overflow: auto;
    padding: 16px 8px 8px;
}

.category-list {
    width: 100%;
    height: 32px;
    border: 1px solid #ddd;
    border-radius: 4px;
    margin-bottom: 6px;
    cursor: pointer;
    text-align: left;
    line-height: 30px;
    padding: 0 10px;
    font-size: 13px;
    background: #fff;
}

.category-list.is-inactive {
    opacity: 0.5;
}

.category-list-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.category-list-count {
    flex-shrink: 0;
    color: #9a9a9a;
}

.category-list.active,
.category-list.sortable-chosen {
    background-color: #000;
    color: #fff;
    border-color: #000;
    opacity: 1;
}

.inline-div {
    display: inline-block;
    position: relative;
    margin: 0 6px 10px 0;
    vertical-align: top;
}

.preview-sidebar-list::-webkit-scrollbar,
.app-area::-webkit-scrollbar {
    display: none;
}

.card {
    position: relative;
}

.items-grid {
    width: 100%;
    margin-bottom: 8px;
    padding-left: 4px;
}

.card-item {
    width: 131px;
    height: 131px;
    border-radius: 8px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    background-color: #fff;
    background-image: linear-gradient(45deg, #e3e3e3 25%, transparent 25%),
        linear-gradient(-45deg, #e3e3e3 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #e3e3e3 75%),
        linear-gradient(-45deg, transparent 75%, #e3e3e3 75%);
    background-size: 12px 12px;
    background-position: 0 0, 0 6px, 6px -6px, -6px 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.card-item .card-media {
    display: block;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.item-badge {
    background-color: rgba(255, 255, 255, 0.6);
    color: black;
    padding-left: 5px;
    padding-right: 5px;
    position: absolute;
    left: 5px;
    font-size: 13px;
    border-radius: 2px;
}

.item-badge.is-id {
    bottom: 5px;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
}

.item-badge.is-dev {
    top: 5px;
    background-color: rgba(255, 193, 7, 0.85);
    font-weight: 600;
    font-size: 11px;
}

.loader {
    position: absolute;
    top: 0px;
    right: 0px;
    width: 100%;
    height: 100%;
    background-color: #eceaea;
    background-image: url('/static/img/oval.svg');
    background-size: 50px;
    background-repeat: no-repeat;
    background-position: center;
    z-index: 10000000;
    opacity: 0.7;
    filter: alpha(opacity=40);
}

@media (max-width: 768px) {
    .preview-layout {
        flex-direction: column;
    }

    .preview-sidebar {
        width: 100%;
    }

    .preview-sidebar-list {
        max-height: 220px;
    }
}
</style>
<style scoped src="../../../assets/css/preview-badges.css"></style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/ResourcePreview.vue